import { useState, useEffect, useRef } from "react";
 
/**
 * Breaks a total number of seconds into { h, m, s } parts.
 */
function toParts(total) {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return { h, m, s };
}

function plural(value, singular, plural)
{
  return value === 1 ? singular : plural;
}
 
/**
 * Formats parts into a human-readable string.
 * Only shows hours/minutes/seconds when non-zero.
 * Examples: "1 hour 7 seconds", "4 minutes 7 seconds", "7 seconds"
 */
export function formatCountdown({ h, m, s }) {
  const parts = [];
  if (h > 0) parts.push(`${h} ${plural(h, 'hour', 'hours')}`);
  if (m > 0) parts.push(`${m} ${plural(m, 'minute', 'minutes')}`);
  if (s > 0) parts.push(`${s} ${plural(s, 'second', 'seconds')}`);
  return parts.join(" ");
}
 
/**
 * Counts down from `seconds` to 0.
 * Returns { remaining, parts, formatted, reset }
 *   remaining  — raw seconds left
 *   parts      — { h, m, s } breakdown
 *   formatted  — e.g. "1 hours 4 minutes 7 seconds", "4 minutes 7 seconds", "7 seconds"
 *   reset      — restart the countdown
 */
export function useCountdown(seconds) {
  const [remaining, setRemaining] = useState(seconds);
  const intervalRef = useRef(null);
 
  const start = (from = seconds) => {
    clearInterval(intervalRef.current);
    setRemaining(from);
    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
 
  useEffect(() => {
    start();
    return () => clearInterval(intervalRef.current);
  }, []);
 
  const parts = toParts(remaining);
 
  return {
    remaining,
    parts,
    formatted: formatCountdown(parts),
    reset: () => start(),
  };
}